import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { css, cx } from '@repo/pandacss/styled-system/css';
import { Badge } from '@repo/ui-v2/Badge';
import { Details } from '@repo/ui-v2/Details';
import { useId } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { renderStructuredContent } from '../../rendererContext';
import type { RendererProps } from '../../types';

export type SMBWhyTrustNerdWalletProps = StructuredContentDocument<{
  children: StructuredContentDocument;
  rendererProps?: RendererProps;
}>;

const expandIconClass = css({
  color: 'neutral.main',
  fontSize: '[30px]',
  width: '[1em]',
  height: '[1em]',
  flexShrink: '[0]',
});

export function SMBWhyTrustNerdWallet({
  props: { children, rendererProps },
}: SMBWhyTrustNerdWalletProps) {
  const idSuffix = useId();
  return (
    <Details
      variant="simple"
      detailsProps={{
        className: css({
          boxShadow: '[none]',
          borderTopWidth: 'thin',
          borderRightWidth: 'thin',
          borderBottomWidth: 'thin',
          borderLeftWidth: 'thin',
          borderTopStyle: 'solid',
          borderRightStyle: 'solid',
          borderBottomStyle: 'solid',
          borderLeftStyle: 'solid',
          borderTopColor: 'neutral.lighter',
          borderRightColor: 'neutral.lighter',
          borderBottomColor: 'neutral.lighter',
          borderLeftColor: 'neutral.lighter',
        }),
      }}
      summaryComponentProps={{
        'aria-controls': `panel-content-smb-why-trust-nerdwallet-${idSuffix}`,
        className: css({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingInlineStart: '2',
          paddingInlineEnd: '2',
          paddingBlockStart: '3',
          paddingBlockEnd: '3',
          _hover: {
            outlineWidth: 'none',
            outlineStyle: 'solid',
            outlineColor: 'blue.light',
            backgroundColor: 'blue.lightest',
            md: {
              outlineWidth: 'medium',
            },
          },
        }),
      }}
      SummaryComponent={
        <>
          <span
            className={css({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              columnGap: '2',
              rowGap: '2',
              marginTop: '1',
              marginRight: '1',
              marginBottom: '1',
              marginLeft: '1',
              paddingLeft: '1',
            })}
          >
            <Badge size={24} />
            <span
              className={css({
                textStyle: 'textBold',
              })}
            >
              Why trust NerdWallet
            </span>
          </span>
          <MdExpandMore className={cx(expandIconClass, 'expand-icon')} aria-hidden="true" />
        </>
      }
    >
      <div
        id={`panel-content-smb-why-trust-nerdwallet-${idSuffix}`}
        className={css({
          paddingTop: '4',
          paddingRight: '4',
          paddingBottom: '4',
          paddingLeft: '4',
        })}
      >
        {renderStructuredContent(children, rendererProps)}
      </div>
    </Details>
  );
}
